<script setup lang="ts">
import { computed } from 'vue'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'

import BaseImage from '@/components/atoms/BaseImage.vue'

const props = defineProps<{
    avatar?: string | null
    name?: string | null
    email?: string | null
    profileUrl?: string | null
    loggingOut?: boolean
}>()

const emit = defineEmits<{
    logout: []
}>()

const avatarUrl = computed(() => props.avatar || '/images/default-avatar.png')
const isDefaultAvatar = computed(() => avatarUrl.value.includes('default-avatar.png'))
</script>

<template>
    <Menu as="div" class="beta-profile-menu">
        <MenuButton
            class="beta-dashboard-user"
            :title="props.name || props.email || 'Abrir perfil'"
            aria-label="Abrir menu do perfil"
        >
            <BaseImage
                :src="avatarUrl"
                alt="Imagem do perfil"
                :external-image="isDefaultAvatar"
                class-name="beta-dashboard-user__image"
            />
        </MenuButton>

        <transition
            enter-active-class="beta-profile-menu__transition"
            enter-from-class="beta-profile-menu__hidden"
            leave-active-class="beta-profile-menu__transition"
            leave-to-class="beta-profile-menu__hidden"
        >
            <MenuItems class="beta-profile-menu__items">
                <div class="beta-profile-menu__identity">
                    <strong>{{ props.name || 'Minha conta' }}</strong>
                    <small v-if="props.email">{{ props.email }}</small>
                </div>

                <MenuItem v-if="props.profileUrl" v-slot="{ active }">
                    <a
                        :href="props.profileUrl"
                        :class="['beta-profile-menu__item', active && 'is-active']"
                    >
                        <span>Perfil</span>
                        <span aria-hidden="true">↗</span>
                    </a>
                </MenuItem>

                <MenuItem v-slot="{ active }">
                    <button
                        type="button"
                        :disabled="props.loggingOut"
                        :class="['beta-profile-menu__item', 'is-logout', active && 'is-active']"
                        @click="emit('logout')"
                    >
                        <span>{{ props.loggingOut ? 'Saindo…' : 'Sair' }}</span>
                        <span aria-hidden="true">→</span>
                    </button>
                </MenuItem>
            </MenuItems>
        </transition>
    </Menu>
</template>

<style scoped>
.beta-profile-menu {
    position: relative;
}

.beta-dashboard-user {
    display: grid;
    width: 38px;
    height: 38px;
    overflow: hidden;
    place-items: center;
    border: 2px solid #fff;
    border-radius: 50%;
    background: #e9eef4;
    outline: 1px solid #dfe6ee;
}

.beta-dashboard-user:focus-visible {
    outline: 2px solid color-mix(in srgb, var(--main-color) 35%, transparent);
    outline-offset: 2px;
}

.beta-dashboard-user :deep(.beta-dashboard-user__image) {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.beta-profile-menu__items {
    position: absolute;
    z-index: 50;
    top: calc(100% + 9px);
    right: 0;
    width: 210px;
    overflow: hidden;
    border: 1px solid #e0e6ed;
    border-radius: 14px;
    background: #fff;
    outline: none;
    padding: 5px;
    transform-origin: top right;
}

.beta-profile-menu__transition {
    transition: 100ms ease;
}

.beta-profile-menu__hidden {
    opacity: 0;
    transform: scale(0.96);
}

.beta-profile-menu__identity {
    min-width: 0;
    border-bottom: 1px solid #edf1f5;
    padding: 9px 10px 10px;
}

.beta-profile-menu__identity strong,
.beta-profile-menu__identity small {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.beta-profile-menu__identity strong {
    color: #2d4561;
    font-size: 11px;
    font-weight: 850;
}

.beta-profile-menu__identity small {
    margin-top: 2px;
    color: #8795a7;
    font-size: 12px;
}

.beta-profile-menu__item {
    display: flex;
    width: 100%;
    min-height: 36px;
    align-items: center;
    justify-content: space-between;
    border-radius: 9px;
    padding: 0 10px;
    color: #40566f;
    font-size: 14px;
    font-weight: 800;
    text-align: left;
}

.beta-profile-menu__item.is-active {
    background: #f2f5f8;
}

.beta-profile-menu__item.is-logout {
    color: #a34e4e;
}

.beta-profile-menu__item:disabled {
    cursor: not-allowed;
    opacity: 0.55;
}

@media (max-width: 767px) {
    .beta-dashboard-user {
        width: 40px;
        height: 40px;
        border-radius: 50%;
    }
}
</style>
